'use client';

import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input"
import {useForm} from "react-hook-form";
import {Button} from "@/components/ui/button";
import {Textarea} from "@/components/ui/textarea";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useCallback} from "react";
import {toast} from "sonner"
import {checkReCaptcha, sendContactUsEmail} from "@/app/_actions";
import Link from "next/link";

export default function ContactUsForm({domain}: { domain: string }) {

    const formSchema = z.object({
        userEmail: z.string().email(),
        name: z.string(),
        message: z.string()
    });


    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            userEmail: '',
            name: '',
            message: ''
        },
    })

    const {executeRecaptcha} = useGoogleReCaptcha();

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return;
        }

        const token = await executeRecaptcha('contactUs');
        return await checkReCaptcha(token, domain);
    }, [executeRecaptcha, domain]);


    async function onSubmit(values: z.infer<typeof formSchema>) {

        const {message, userEmail, name} = values;
        try {
            if (await handleReCaptchaVerify()) {
                await sendContactUsEmail({
                    site: domain,
                    message: message.trim(),
                    userEmail: userEmail.toLowerCase().trim(),
                    name
                })
                toast('Thank you for your submission!');
                form.reset();
            } else {
                toast(`Possible bot activity. Please try again or contact ${process.env.NEXT_PUBLIC_FEATURED_EMAIL} for support`);
            }


        } catch {
            toast(`An error occurred. Please try again or contact ${process.env.NEXT_PUBLIC_FEATURED_EMAIL} for support`);
        }
    }

    return (
        <div className={'my-auto h-full bg-card-base p-16 w-full flex flex-col gap-8 items-center'}>
            <h1 className={'text-3xl'}>Contact Us</h1>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <FormField
                        control={form.control}
                        name="userEmail"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>User Email</FormLabel>
                                <FormControl>
                                    <Input placeholder="user@example.com" {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="name"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                    <Input placeholder="John Doe" {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="message"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Message</FormLabel>
                                <FormControl>
                                    <Textarea placeholder={'Type your message here…'} {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <span
                        className={'text-xs leading-none py-4'}>This site is protected by reCAPTCHA and the Google&nbsp;
                        <Link className={'text-primary'} target={'_blank'} rel={'noopener noreferrer'}
                              href="https://policies.google.com/privacy"><strong>Privacy Policy</strong></Link>&nbsp;and&nbsp;
                        <Link
                            className={'text-primary'}
                            href="https://policies.google.com/terms" target={'_blank'}
                            rel={'noopener noreferrer'}><strong>Terms of Service</strong></Link>&nbsp;apply.
                </span>

                    <Button type="submit" className={'bg-secondary-lighter text-primary'}>Submit</Button>
                </form>
            </Form>
        </div>
    )
}